import { createSelector } from 'reselect';

import { Campaign, UpcomingCampaign } from '@shared/types/mylounge';
import { CampaignFilterTags, CategoryTabIds } from '@client/components/mylounge/banner/constants';
import { RootState } from '@client/redux/types';

import { PLUS_EARLY_ACCESS_TAB_ID } from '@client/config/constants';
import { NAMESPACE } from './constants';

export const pickEarlyAccess = ({
    plusEarlyAccess,
    // @ts-ignore
    // eslint-disable-next-line camelcase
    plus_early_access,
}: // eslint-disable-next-line camelcase
Campaign | UpcomingCampaign) => !!plusEarlyAccess || !!plus_early_access;

const filterEarlyAccess = (campaign: Campaign | UpcomingCampaign) => !pickEarlyAccess(campaign);

const filterTabCampaigns = (categoryTabId: CategoryTabIds) => (campaign: Campaign) =>
    campaign.tabIds.includes(categoryTabId);

const filterCampaignsByFreshness =
    (filter = CampaignFilterTags.ALL) =>
    (campaign: Campaign) => {
        if (filter === CampaignFilterTags.ALL) {
            return true;
        }

        if (
            filter === CampaignFilterTags.STARTING_TODAY &&
            new Date(campaign.startsAt).getDate() === new Date().getDate()
        ) {
            return true;
        }

        if (
            filter === CampaignFilterTags.ENDING_TODAY &&
            new Date(campaign.endsAt).getDate() === new Date().getDate()
        ) {
            return true;
        }

        return false;
    };

export const getMylounge = (state: RootState) => state[NAMESPACE];

export const getCategoryTabId = (state: RootState) => getMylounge(state).categoryTabId;

export const getCampaignFilterTag = (state: RootState) => getMylounge(state).filterTag;

export const openCampaignsSelector = (state: RootState) => getMylounge(state).openCampaigns;

const upcomingCampaignsSelector = (state: RootState) => getMylounge(state).upcomingCampaigns;

export const getOpenCampaigns = createSelector(
    openCampaignsSelector,
    getCategoryTabId,
    getCampaignFilterTag,
    (items: Campaign[] = [], categoryTabId, filterTag) => {
        /**
         * TODO: Should we consider not hiding EA campaigns when the Plus feature is not enabled?
         */
        if (categoryTabId === CategoryTabIds.ALL) {
            return items.filter(
                campaign =>
                    filterTabCampaigns(categoryTabId)(campaign) &&
                    filterEarlyAccess(campaign) &&
                    filterCampaignsByFreshness(filterTag)(campaign),
            );
        }

        return items.filter(filterTabCampaigns(categoryTabId));
    },
);

export const hasOpenCampaigns = createSelector(
    openCampaignsSelector,
    (items: Campaign[] = []) => items && items.length !== 0,
);

export const getEarlyAccessCampaigns = createSelector(
    openCampaignsSelector,
    upcomingCampaignsSelector,
    (openCampaigns: Campaign[] = [], upcomingCampaigns: UpcomingCampaign[] = []) => {
        const earlyAccessOpenCampaigns = openCampaigns.filter(pickEarlyAccess);

        const earlyAccessUpcomingCampaigns = upcomingCampaigns.filter(pickEarlyAccess);

        return {
            openCampaigns: earlyAccessOpenCampaigns,
            upcomingCampaigns: earlyAccessUpcomingCampaigns,
        };
    },
);

export const getShowstopperEarlyAccessCampaigns = createSelector(
    getEarlyAccessCampaigns,
    campaigns => {
        const { openCampaigns, upcomingCampaigns } = campaigns;

        return {
            openCampaigns: openCampaigns
                .filter(campaign => campaign.tabIds.includes(CategoryTabIds.ALL))
                .slice(0, 1),
            upcomingCampaigns: upcomingCampaigns.slice(0, 1),
        };
    },
);

export const makeGetOpenCampaignById = () =>
    createSelector(
        openCampaignsSelector,
        (_: RootState, campaignId: string) => campaignId,
        (openCampaigns: Campaign[] = [], campaignId) => {
            return openCampaigns.find(campaign => campaign.campaignId === campaignId);
        },
    );

export const makeGetUpcomingCampaigns = () =>
    createSelector(
        upcomingCampaignsSelector,
        getEarlyAccessCampaigns,
        getCategoryTabId,
        (_: RootState, isPlusSignupFlowEnabled: boolean) => isPlusSignupFlowEnabled,
        (
            items: UpcomingCampaign[] = [],
            { openCampaigns, upcomingCampaigns },
            categoryTabId,
            isPlusSignupFlowEnabled,
        ) => {
            if (
                isPlusSignupFlowEnabled &&
                categoryTabId === CategoryTabIds.ALL &&
                openCampaigns.length === 0 &&
                upcomingCampaigns.length !== 0
            ) {
                return items.filter(filterEarlyAccess);
            }

            return items;
        },
    );

export const hasEarlyAccessCampaigns = createSelector(
    getEarlyAccessCampaigns,
    ({ openCampaigns, upcomingCampaigns }) =>
        openCampaigns.length !== 0 || upcomingCampaigns.length !== 0,
);

export const getTabs = (state: RootState) => state[NAMESPACE].tabs;

export const hasPlusEarlyAccessTab = createSelector(getTabs, tabs => {
    return tabs.findIndex(tab => tab.id === PLUS_EARLY_ACCESS_TAB_ID) !== -1;
});

export const getShowstoppers = (state: RootState) => state[NAMESPACE].showstoppers;

export const getIsLoading = (state: RootState) => state[NAMESPACE].loading;

export const getError = (state: RootState) => state[NAMESPACE].error;

export const getImagesLoaded = (state: RootState) => state[NAMESPACE].imagesLoaded;
